/**
 * starForgePipeline — multiple protostars forming with accretion
 * disks, jets, background nebulosity, and periodic nova explosions.
 *
 * Composition:
 *   1. Background — colorful nebula with multiple gas clouds (FBM)
 *   2. Distant starfield — fine sparkle points
 *   3. Six protostars, each with own phase:
 *      - central glow that pulses with own rhythm
 *      - accretion disk rotating around it
 *      - bipolar jets shooting out
 *   4. Background nebulosity — soft glowing gas clouds
 *   5. Periodic nova explosions — bright flashes every ~8 seconds
 *      with expanding shockwave rings
 *   6. Dust lanes — dark wisps in the nebula
 *
 * Audio reactivity:
 *   bass — brightens the brightest protostar, expands nova rings
 *   mid  — speeds accretion disks, intensifies jet length
 *   high — flickers star sparkle, drives dust shimmer
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface StarForgePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float h11(float n) {
  return fract(sin(n * 91.345) * 47453.12);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float r = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    r += a * noise2(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return r;
}

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // --- 1. Background nebula ---
  float n1 = fbm(p * 1.8 + vec2(uTime * 0.04, uTime * 0.03));
  float n2 = fbm(p * 3.0 + vec2(-uTime * 0.05, uTime * 0.07));
  float n3 = fbm(p * 6.0 + vec2(uTime * 0.02, -uTime * 0.04));
  vec3 nebulaCol = vec3(0.0);
  nebulaCol += vec3(0.30, 0.10, 0.50) * n1 * 0.6;
  nebulaCol += vec3(0.05, 0.15, 0.45) * n2 * 0.5;
  nebulaCol += vec3(0.50, 0.20, 0.10) * n3 * 0.4;

  // Base space color.
  vec3 spaceCol = mix(vec3(0.01, 0.005, 0.04), vec3(0.05, 0.02, 0.12), vUV.y);

  vec3 col = spaceCol + nebulaCol;

  // --- 2. Starfield ---
  vec2 sp = p * 120.0;
  vec2 si = floor(sp);
  vec2 sf = fract(sp);
  float starSeed = hash21(si);
  if (starSeed > 0.985) {
    vec2 c = vec2(0.5);
    float starD = length(sf - c);
    float star = exp(-starD * 200.0);
    star *= 0.4 + 0.6 * sin(uTime * 2.0 + starSeed * 30.0);
    vec3 starColor = mix(vec3(1.0, 0.85, 0.6), vec3(0.7, 0.85, 1.0), starSeed);
    col += starColor * star * (0.7 + high * 0.6);
  }

  // --- 3. Six protostars ---
  // Predefined positions on screen with offsets.
  vec2 starPos[6];
  starPos[0] = vec2(-0.45, 0.20);
  starPos[1] = vec2(0.30, 0.32);
  starPos[2] = vec2(-0.10, -0.10);
  starPos[3] = vec2(0.45, -0.30);
  starPos[4] = vec2(-0.40, -0.45);
  starPos[5] = vec2(0.05, 0.50);

  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    vec2 sp2 = starPos[i] + vec2(
      sin(uTime * (0.2 + fi * 0.05)) * 0.02,
      cos(uTime * (0.3 + fi * 0.07)) * 0.02
    );
    float dStar = length(p - sp2);
    float phase = h11(fi * 7.13);
    float cycle = sin(uTime * (0.3 + phase * 0.7) + phase * 6.28);
    float bright = 0.4 + 0.6 * cycle * (0.5 + bass * 0.5);
    float radius = 0.025 + bright * 0.02;

    // Accretion disk.
    float ang = atan(p.y - sp2.y, p.x - sp2.x);
    float rot = uTime * (1.0 + mid * 2.0 + phase * 0.5);
    float spiral = sin(ang * 6.0 + rot + length(p - sp2) * 30.0);
    float diskMask = smoothstep(radius * 4.5, radius * 1.1, dStar) * smoothstep(radius * 0.5, radius * 1.5, dStar);
    vec3 diskCol = mix(
      vec3(1.0, 0.7, 0.3),
      vec3(0.5, 0.3, 1.0),
      phase
    );
    col += diskCol * (0.4 + spiral * 0.4) * diskMask * (0.6 + mid * 0.5);

    // Central core.
    float core = exp(-dStar / radius);
    float coreCore = exp(-dStar / (radius * 0.3));
    vec3 coreCol = mix(
      vec3(1.0, 0.95, 0.85),
      vec3(0.7, 0.85, 1.0),
      phase
    );
    col += coreCol * (coreCore * (2.0 + bright * 2.0) + core * 0.5);

    // Bipolar jets.
    float jetLen = 0.30 + 0.15 * sin(uTime * 2.0 + phase * 5.0);
    jetLen *= (0.6 + mid * 0.7);
    // Two jet directions perpendicular to disk plane. Approximated as up/down.
    vec2 j1 = vec2(sp2.x + sin(phase * 6.28) * 0.1, sp2.y + jetLen);
    vec2 j2 = vec2(sp2.x - sin(phase * 6.28) * 0.1, sp2.y - jetLen);
    float dj1 = length(p - j1);
    float dj2 = length(p - j2);
    float jet1 = exp(-dj1 * 60.0);
    float jet2 = exp(-dj2 * 60.0);
    // Also draw a polyline for the jets.
    for (int s = 0; s < 8; s++) {
      float fs = float(s) / 7.0;
      vec2 jp1 = mix(sp2, j1, fs);
      vec2 jp2 = mix(sp2, j2, fs);
      float djp1 = length(p - jp1);
      float djp2 = length(p - jp2);
      jet1 = max(jet1, exp(-djp1 * 80.0));
      jet2 = max(jet2, exp(-djp2 * 80.0));
    }
    vec3 jetCol = mix(vec3(0.5, 0.8, 1.0), vec3(1.0, 0.6, 0.4), phase);
    col += jetCol * (jet1 + jet2) * (0.5 + mid * 0.5);
  }

  // --- 4. Periodic nova explosions ---
  // Nova every ~8 seconds. Three independent nova cycles.
  for (int i = 0; i < 3; i++) {
    float fi = float(i);
    float cycleT = mod(uTime + fi * 2.7, 8.0);
    vec2 novaPos = vec2(
      sin(fi * 4.0) * 0.5 + cos(uTime * 0.1 + fi) * 0.1,
      cos(fi * 3.0) * 0.4 + sin(uTime * 0.13 + fi) * 0.1
    );
    float dNova = length(p - novaPos);
    float novaPhase = cycleT / 8.0;
    // Expanding ring.
    float ringR = novaPhase * 0.7;
    float ringT = exp(-pow(dNova - ringR, 2.0) * 800.0);
    float ringFade = 1.0 - novaPhase;
    vec3 novaCol = mix(vec3(1.0, 0.9, 0.6), vec3(0.6, 0.8, 1.0), h11(fi * 3.1));
    col += novaCol * ringT * ringFade * (0.5 + bass * 1.5);
    // Central flash at t=0.
    float flash = exp(-cycleT * 4.0) * exp(-dNova * 30.0);
    col += vec3(1.0, 0.95, 0.85) * flash * (0.6 + bass * 1.0);
  }

  // --- 5. Dust lanes (dark wisps) ---
  float dust = fbm(p * 4.0 + vec2(uTime * 0.03, -uTime * 0.04));
  dust = smoothstep(0.55, 0.75, dust);
  col -= vec3(0.05, 0.03, 0.08) * dust * 0.6;

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.3;

  // Tone mapping + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeStarForgeBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.6);
  out[1] = 0.5 + 0.4 * Math.abs(Math.sin(t * 1.2));
  out[2] = 0.4 + 0.4 * Math.sin(t * 2.0 + 0.7);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.0 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.6 + 2.3);
  out[5] = 0.4 + 0.5 * Math.sin(t * 2.4 + 3.1);
  out[6] = 0.3 + 0.6 * Math.sin(t * 4.0 + 0.5);
  out[7] = 0.4 + 0.5 * Math.sin(t * 6.0 + 1.7);
  out[8] = 0.3 + 0.6 * Math.sin(t * 8.0 + 2.1);
}

export function computeStarForgeRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.012], [0.012, 0.040], [0.040, 0.080],
    [0.080, 0.180], [0.180, 0.300], [0.300, 0.460],
    [0.460, 0.640], [0.640, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0;
    let count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createStarForgePipeline(gl: WebGL2RenderingContext): StarForgePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    bands: new Float32Array(9),
  };
}

export function drawStarForge(
  gl: WebGL2RenderingContext,
  pipe: StarForgePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}